import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { loadLetterhead } from '../../../utils/pdfLetterhead';
import { exportFilename } from '../../../utils/nomenclature';
import GaDashboardCanvas, { STATUS_COLORS } from './GaDashboardCanvas';
import ActivityPicker from './ActivityPicker';
import SharePointBrowser from './SharePointBrowser';
import { RENDER_QUALITY_OPTIONS, getRenderQualityId, setRenderQualityId, getRenderQuality } from './renderQuality';
import {
  makeGaBlobFetcher,
  getCachedGaDrawing,
  getCachedGaPreview,
  prefetchDashboardDrawing,
  prefetchGaPreview,
  forgetGaDrawing,
} from './gaDrawingCache';
import GaSequenceScroller from './GaSequenceScroller';
import GaPdfExportModal from './GaPdfExportModal';
import {
  pagesToExport,
  buildMetaBits,
  measureChrome,
  waitFor,
  loadImageDataUrl,
  makePlaceholderCanvas,
  stampGaPdfPage,
  captureGaStage,
} from './gaPdfExport';
import {
  buildLabelStyleAfterMove,
  clampBalloonScale,
  displayMarkerLabel,
  effectiveLabelMode,
  isPlaceholderMarkerLabel,
  markerBalloonScale,
  markerShowFields,
  offsetModeKey,
  SHOW_FIELD_KEYS,
} from './gaBalloonUtils';

const fmt = (d) => (d ? String(d).slice(0, 10) : null);

function BalloonSizeSlider({ value, onInput, onCommit, label = 'Size', title }) {
  const pct = Math.round(clampBalloonScale(value) * 100);
  return (
    <label
      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}
      title={title || 'Scale balloons from 70% to 200%'}
    >
      {label}
      <input
        type="range"
        min={70}
        max={200}
        step={5}
        value={pct}
        onChange={(e) => onInput(Number(e.target.value) / 100)}
        onMouseUp={(e) => onCommit(Number(e.target.value) / 100)}
        onTouchEnd={(e) => onCommit(Number(e.target.value) / 100)}
        onKeyUp={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') {
            onCommit(Number(e.target.value) / 100);
          }
        }}
        style={{ width: 92, cursor: 'pointer', accentColor: '#0f766e' }}
      />
      <span style={{ fontWeight: 600, color: '#334155', minWidth: 36 }}>{pct}%</span>
    </label>
  );
}

/**
 * GaDashboardView - one dashboard: GA drawing + schedule overlays.
 * Toolbar: snapshot ("based on update") selector, compare, edit mode,
 * time scrubber with play, presentation mode and PNG export.
 */
const GaDashboardView = ({ jobNumber, jobName, projectId, dashboard, snapshots, onBack, onDashboardChanged, onOpenHelp }) => {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null); // { dashboard, snapshot, markers, resolved, compare }
  const [rev, setRev] = useState(''); // P6 proj_id — empty = server default (live Current)
  const [snapshotId, setSnapshotId] = useState(null); // imported snapshot override (null = prefer P6)
  const [scheduleSource, setScheduleSource] = useState('p6'); // 'p6' | 'snapshot'
  const [compareId, setCompareId] = useState(''); // snapshot compare
  const [compareRev, setCompareRev] = useState(''); // P6-vs-P6 compare
  // Default resource assignments (LGL schedules are resource-loaded). Dates always from activity.
  const [unitsMode, setUnitsMode] = useState(dashboard.settings?.unitsMode === 'task' ? 'task' : 'taskrsrc');
  const [pctMode, setPctMode] = useState(dashboard.settings?.pctMode === 'average' ? 'average' : 'weighted');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [tool, setTool] = useState('select');
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showSpBrowser, setShowSpBrowser] = useState(false);
  const [drawingUrl, setDrawingUrl] = useState(() => getCachedGaDrawing(dashboard.id)?.url || null);
  const [previewUrl, setPreviewUrl] = useState(() => getCachedGaPreview(dashboard.id)?.url || null);
  const [presentation, setPresentation] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [labelMode, setLabelMode] = useState(dashboard.settings?.labelMode || 'margins');
  const [boardBalloonScale, setBoardBalloonScale] = useState(() => clampBalloonScale(dashboard.settings?.balloonScale));
  const [renderQualityId, setRenderQualityIdState] = useState(() => getRenderQualityId());
  const [pageMeta, setPageMeta] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const changeRenderQuality = (id) => {
    setRenderQualityId(id);
    setRenderQualityIdState(id);
  };

  // Time scrubber
  const [viewDate, setViewDate] = useState(null); // null = live (data date)
  const [playing, setPlaying] = useState(false);
  const playTimer = useRef(null);

  const fileInputRef = useRef(null);
  const stageRef = useRef(null);
  const sequenceExportRef = useRef(null);
  const rootRef = useRef(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(null);

  const fetchResolved = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (scheduleSource === 'snapshot' && snapshotId) {
        params.set('source', 'snapshot');
        params.set('snapshotId', snapshotId);
      } else {
        params.set('source', 'p6');
        if (rev) params.set('rev', rev);
      }
      if (compareRev) params.set('compareRev', compareRev);
      else if (compareId) params.set('compareId', compareId);
      params.set('unitsMode', unitsMode);
      params.set('pctMode', pctMode);
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/ga-dashboards/dashboards/${dashboard.id}/resolved?${params.toString()}`
      );
      const payload = res.data.data;
      setData(payload);
      if (payload?.dashboard?.settings?.balloonScale != null) {
        setBoardBalloonScale(clampBalloonScale(payload.dashboard.settings.balloonScale));
      }
      // Sync rev from server default on first P6 load
      if (payload?.source === 'p6' && payload.selectedRev && !rev) {
        setRev(String(payload.selectedRev));
      }
      if (payload?.source === 'snapshot') setScheduleSource('snapshot');
      if (payload?.source === 'p6') setScheduleSource('p6');
    } catch (e) {
      console.error('Failed to load dashboard:', e);
      setError(e.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, dashboard.id, snapshotId, compareId, compareRev, rev, scheduleSource, unitsMode, pctMode]);

  useEffect(() => { fetchResolved(); }, [fetchResolved]);

  // Drawing + HQ thumb start immediately from the list card — do not wait for P6 /resolved.
  const fetchBlob = useCallback(
    makeGaBlobFetcher(authenticatedApiCall, instance, accounts),
    [instance, accounts]
  );

  useEffect(() => {
    const hasDrawing = !!(dashboard.drawing_path || data?.dashboard?.drawing_path);
    if (!hasDrawing) {
      setDrawingUrl(null);
      setPreviewUrl(null);
      return undefined;
    }
    const cachedDraw = getCachedGaDrawing(dashboard.id);
    if (cachedDraw?.url) setDrawingUrl(cachedDraw.url);
    const cachedPrev = getCachedGaPreview(dashboard.id);
    if (cachedPrev?.url) setPreviewUrl(cachedPrev.url);

    let cancelled = false;
    prefetchGaPreview(fetchBlob, dashboard.id, { version: dashboard.updated_at })
      .then((entry) => { if (!cancelled && entry?.url) setPreviewUrl(entry.url); })
      .catch((e) => console.warn('GA preview failed:', e.message));
    authenticatedApiCall(instance, accounts, 'GET', `/ga-dashboards/dashboards/${dashboard.id}/drawing/meta`)
      .then((res) => {
        if (cancelled) return;
        const meta = res.data?.data;
        if (meta) {
          setPageMeta(meta);
          setActivePage(meta.drawingPage || 1);
        }
      })
      .catch(() => { if (!cancelled) setPageMeta({ pageCount: 1, viewMode: 'single' }); });
    prefetchDashboardDrawing(fetchBlob, dashboard)
      .then((entry) => { if (!cancelled && entry?.url) setDrawingUrl(entry.url); })
      .catch((e) => console.error('Failed to load drawing:', e));
    return () => { cancelled = true; };
  }, [dashboard.id, dashboard.drawing_path, dashboard.updated_at, data?.dashboard?.drawing_path, data?.dashboard?.updated_at, fetchBlob, instance, accounts]);

  const resolvedById = useMemo(() => {
    const map = {};
    for (const r of data?.resolved || []) map[r.markerId] = r;
    return map;
  }, [data]);

  const compareById = useMemo(() => {
    const map = {};
    for (const d of data?.compare?.deltas || []) map[d.markerId] = d;
    return map;
  }, [data]);

  const markers = data?.markers || [];
  const selectedMarker = markers.find((m) => m.id === selectedMarkerId) || null;
  const dataDate = fmt(data?.snapshot?.data_date);
  const p6Revisions = data?.revisions || [];

  // Plan window for the scrubber
  const planWindow = useMemo(() => {
    const starts = (data?.resolved || []).map((r) => r.start).filter(Boolean);
    const ends = (data?.resolved || []).map((r) => r.finish).filter(Boolean);
    if (!starts.length) return null;
    return { min: starts.sort()[0], max: ends.sort()[ends.length - 1] };
  }, [data]);

  const dayCount = planWindow ? Math.max(1, Math.round((new Date(planWindow.max) - new Date(planWindow.min)) / 86400000)) : 0;
  const dayIndex = viewDate && planWindow ? Math.round((new Date(viewDate) - new Date(planWindow.min)) / 86400000) : dayCount;

  const setDayIndex = (i) => {
    if (!planWindow) return;
    const d = new Date(new Date(planWindow.min).getTime() + i * 86400000);
    setViewDate(d.toISOString().slice(0, 10));
  };

  useEffect(() => {
    if (!playing) { clearInterval(playTimer.current); return undefined; }
    playTimer.current = setInterval(() => {
      setViewDate((prev) => {
        if (!planWindow) return prev;
        const cur = prev || planWindow.min;
        const next = new Date(new Date(cur).getTime() + 7 * 86400000); // one week per tick
        const nextStr = next.toISOString().slice(0, 10);
        if (nextStr >= planWindow.max) { setPlaying(false); return planWindow.max; }
        return nextStr;
      });
    }, 350);
    return () => clearInterval(playTimer.current);
  }, [playing, planWindow]);

  // ---- marker CRUD ----
  const createMarker = async (geometry, kindHint, pageNumber) => {
    try {
      const kind = kindHint === 'zone' ? 'zone' : kindHint === 'callout' ? 'callout' : 'pin';
      const page = Math.max(1, parseInt(pageNumber, 10) || activePage || 1);
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/dashboards/${dashboard.id}/markers`,
        { kind, label: `Area ${markers.length + 1}`, geometry, style: { page }, binding: {} }
      );
      setSelectedMarkerId(res.data.data.id);
      setTool('select');
      setShowPicker(true);
      await fetchResolved();
    } catch (e) {
      console.error('Failed to create marker:', e);
    }
  };

  const updateMarker = async (markerId, patch) => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/ga-dashboards/markers/${markerId}`, patch);
      await fetchResolved();
    } catch (e) {
      console.error('Failed to update marker:', e);
    }
  };

  // Balloon positions are stored PER mode so switching outside ↔ on-drawing
  // never wipes what the user placed. style.labelOffsets = { margins, auto }.
  // Sequence slides force 'auto' on the canvas while the toolbar still says
  // "outside" — always write the slot the canvas actually used (modeFromCanvas).
  const moveLabel = async (markerId, labelOffset, modeFromCanvas) => {
    const marker = markers.find((m) => m.id === markerId);
    if (!marker) return;
    const pageCountNow = pageMeta?.pageCount || Number((data?.dashboard || dashboard).settings?.pageCount) || 1;
    const modeKey = modeFromCanvas
      || offsetModeKey(effectiveLabelMode(labelMode, { isSequence: pageCountNow > 1 }));
    const nextStyle = buildLabelStyleAfterMove(marker.style, labelOffset, modeKey);
    setData((prev) => ({
      ...prev,
      markers: prev.markers.map((m) => (m.id === markerId ? { ...m, style: nextStyle } : m)),
    }));
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/ga-dashboards/markers/${markerId}`, {
        style: nextStyle,
      });
    } catch (e) {
      console.error('Failed to save label position:', e);
    }
  };

  const changeLabelMode = async (mode) => {
    // Only switch the layout mode — never clear user-dragged positions.
    setLabelMode(mode);
    try {
      await authenticatedApiCall(instance, accounts, 'PATCH', `/ga-dashboards/dashboards/${dashboard.id}`, {
        settings: { ...(data?.dashboard?.settings || {}), labelMode: mode },
      });
    } catch (e) {
      console.error('Failed to save label mode:', e);
    }
  };

  const resetBalloonPositions = async () => {
    // Reset the mode the canvas is drawing. Sequence slides also drop the
    // leftover `margins` slot from before gutters were removed, or Reset
    // would appear to do nothing (fallback would restore the old positions).
    const pageCountNow = pageMeta?.pageCount || Number((data?.dashboard || dashboard).settings?.pageCount) || 1;
    const isSequenceBoard = pageCountNow > 1;
    const modeKey = offsetModeKey(effectiveLabelMode(labelMode, { isSequence: isSequenceBoard }));
    const extraKeys = isSequenceBoard && modeKey === 'auto' ? ['margins'] : [];
    const nextStyleFor = (m) => {
      const offsets = { ...(m.style?.labelOffsets || {}) };
      delete offsets[modeKey];
      extraKeys.forEach((k) => delete offsets[k]);
      const { labelOffset, labelOffsetMode, ...rest } = m.style || {};
      const next = { ...rest, labelOffsets: offsets };
      if (labelOffsetMode && labelOffsetMode !== modeKey && !extraKeys.includes(labelOffsetMode) && labelOffset) {
        next.labelOffset = labelOffset;
        next.labelOffsetMode = labelOffsetMode;
      }
      return next;
    };
    setData((prev) => ({
      ...prev,
      markers: prev.markers.map((m) => ({ ...m, style: nextStyleFor(m) })),
    }));
    try {
      await Promise.all(
        markers.map((m) => authenticatedApiCall(instance, accounts, 'PUT', `/ga-dashboards/markers/${m.id}`, {
          style: nextStyleFor(m),
        }))
      );
    } catch (e) {
      console.error('Failed to reset balloon positions:', e);
    }
  };

  const deleteMarker = async (markerId) => {
    if (!window.confirm('Delete this marker?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/ga-dashboards/markers/${markerId}`);
      setSelectedMarkerId(null);
      await fetchResolved();
    } catch (e) {
      console.error('Failed to delete marker:', e);
    }
  };

  // ---- drawing upload ----
  // "Add slides" appends. "Replace drawing" swaps the file, and a shorter
  // file is refused by the server unless this confirm already ran.
  const drawingIntentRef = useRef('replace');
  const chooseDrawingFile = (intent) => {
    drawingIntentRef.current = intent;
    fileInputRef.current?.click();
  };

  const uploadDrawing = async (file, intent = drawingIntentRef.current) => {
    const slides = pageMeta?.pageCount || Number((data?.dashboard || dashboard).settings?.pageCount) || 1;
    if (intent === 'replace' && slides > 1) {
      const ok = window.confirm(
        `Replace all ${slides} slides with "${file.name}"?\n\n`
        + 'That removes them from the board. The previous file is kept on the server so it can be put back. '
        + 'Pins stay tied to their old slide numbers.\n\n'
        + 'To keep these slides and put this file at the end, choose Add slides instead.'
      );
      if (!ok) return;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (intent === 'replace') formData.append('confirmReplace', '1');
      const path = intent === 'append'
        ? `/ga-dashboards/dashboards/${dashboard.id}/drawing/append`
        : `/ga-dashboards/dashboards/${dashboard.id}/drawing`;
      await authenticatedApiCall(
        instance, accounts, 'POST', path,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      forgetGaDrawing(dashboard.id);
      setDrawingUrl(null);
      setPageMeta(null);
      await fetchResolved();
      onDashboardChanged?.();
    } catch (e) {
      const blocked = e.response?.status === 409 && e.response?.data?.code === 'drawing_replace_blocked';
      if (blocked) {
        const add = window.confirm(
          `${e.response.data.error}\n\nOK adds this file after the current slides. Cancel leaves the board as it is.`
        );
        if (add) return uploadDrawing(file, 'append');
        return;
      }
      alert(e.response?.data?.error || 'Failed to upload drawing');
    }
  };

  const pickFromSharePoint = async ({ sitePath, driveName, itemId }) => {
    setShowSpBrowser(false);
    const slides = pageMeta?.pageCount || Number((data?.dashboard || dashboard).settings?.pageCount) || 1;
    if (slides > 1) {
      const ok = window.confirm(
        `Replace all ${slides} slides with the SharePoint file?\n\n`
        + 'The previous deck is archived on the server. To add pages instead, download the file and use Add slides.'
      );
      if (!ok) return;
    }
    try {
      await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/dashboards/${dashboard.id}/drawing/sharepoint`,
        { sitePath, driveName, itemId, confirmReplace: true }
      );
      forgetGaDrawing(dashboard.id);
      setDrawingUrl(null);
      setPageMeta(null);
      await fetchResolved();
      onDashboardChanged?.();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to import drawing from SharePoint');
    }
  };

  const persistDashboardSettings = async (patch) => {
    const next = { ...(data?.dashboard?.settings || dashboard.settings || {}), ...patch };
    try {
      await authenticatedApiCall(instance, accounts, 'PATCH', `/ga-dashboards/dashboards/${dashboard.id}`, { settings: next });
      setData((prev) => (prev ? { ...prev, dashboard: { ...prev.dashboard, settings: next } } : prev));
      onDashboardChanged?.();
    } catch (e) {
      console.error('Failed to save dashboard settings:', e);
    }
  };

  const commitBoardBalloonScale = (scale) => {
    const next = clampBalloonScale(scale);
    setBoardBalloonScale(next);
    persistDashboardSettings({ balloonScale: next });
  };

  // ---- export ----
  const exportPng = async () => {
    if (!stageRef.current) return;
    try {
      setExporting(true);
      const canvas = await captureGaStage(stageRef.current, html2canvas, { scale: 2 });
      const link = document.createElement('a');
      link.download = `${dashboard.name.replace(/[^\w-]+/g, '_')}_${dataDate || 'latest'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const pdfPreview = useMemo(() => {
    const selectedP6 = (data?.revisions || []).find(
      (r) => String(r.id) === String(data?.selectedRev)
        || String(r.projId) === String(data?.selectedRev)
    );
    const p6Short =
      selectedP6?.projShortName
      || selectedP6?.label
      || data?.snapshot?.project_short_name
      || null;
    const projectLine = [jobNumber, jobName].filter(Boolean).join(' — ') || `Job ${jobNumber || ''}`;
    const portalPath = projectId
      ? `/project/${projectId}#schedule-ga-dashboards`
      : '/projects';
    const portalUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://portal.laframboise.net'}${portalPath}`;
    const printedAt = new Date().toLocaleString('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    return {
      companyHeader: `${projectLine}\nGA Dashboard — ${dashboard.name}\nLaframboise Group Ltd.`,
      estimateP6: p6Short ? `Estimate / P6 ${p6Short}` : 'Estimate / P6 (none on this Rev)',
      dataDate: dataDate ? `Data date ${dataDate}` : 'Data date (none)',
      source: data?.source === 'p6' ? 'Live P6' : 'Imported snapshot',
      labor: unitsMode === 'taskrsrc' ? 'Labor: resource (TASKRSRC)' : 'Labor: activity (TASK)',
      pct: pctMode === 'average' ? 'Pct: average' : 'Pct: weighted Act÷Bud',
      legend: 'Not started · In progress · Complete · Late',
      printed: `Printed ${printedAt}`,
      portalUrl,
      portalHost: 'portal.laframboise.net',
      p6Short,
      projectLine,
      portalPath,
    };
  }, [data, jobNumber, jobName, projectId, dashboard.name, dataDate, unitsMode, pctMode]);

  const exportPdf = async (opts) => {
    const count = pageMeta?.pageCount || Number((data?.dashboard || dashboard).settings?.pageCount) || 1;
    const sequence = count > 1;
    const pages = pagesToExport({
      pageScope: opts.pageScope,
      pageCount: count,
      activePage,
    });
    try {
      setExporting(true);
      setPdfProgress({ current: pages[0] || 1, total: pages.length });

      let captures;
      if (sequence && sequenceExportRef.current?.capturePages) {
        captures = await sequenceExportRef.current.capturePages(pages, {
          onProgress: (n) => setPdfProgress({ current: n, total: pages.length }),
        });
      } else {
        await waitFor(() => stageRef.current?.getAttribute('data-ga-ready') === '1').catch(() => {});
        if (!stageRef.current) throw new Error('Drawing is not ready');
        const canvas = await captureGaStage(stageRef.current, html2canvas, { scale: 2 });
        captures = [{ page: pages[0] || 1, canvas }];
      }

      const layout = measureChrome(opts);
      const items = layout.items;
      let logoDataUrl = null;
      let legalName = 'Laframboise Group Ltd.';
      if (opts.includeChrome && items.companyHeader) {
        try {
          logoDataUrl = await loadImageDataUrl('/lgl-logo-cropped.png');
        } catch {
          logoDataUrl = null;
        }
        try {
          const resolved = await loadLetterhead({ instance, accounts, jobNumber, projectId });
          legalName = resolved?.letterhead?.legal_name || legalName;
        } catch {
          /* keep default */
        }
      }

      const printedAt = new Date().toLocaleString('en-CA', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      });
      const portalUrl = `${window.location.origin}${pdfPreview.portalPath}`;
      const metaBits = buildMetaBits(items, {
        p6Short: pdfPreview.p6Short,
        dataDate,
        source: data?.source,
        unitsMode,
        pctMode,
      });

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
      let failed = 0;
      captures.forEach((cap, i) => {
        const canvas = cap.canvas || makePlaceholderCanvas(cap.page, cap.error);
        if (!cap.canvas) failed += 1;
        stampGaPdfPage(pdf, {
          canvas,
          layout,
          addPage: i > 0,
          ctx: {
            logoDataUrl,
            projectLine: pdfPreview.projectLine,
            dashboardName: dashboard.name,
            legalName,
            printedAt,
            metaBits,
            portalUrl,
            portalHost: 'portal.laframboise.net',
            statusColors: STATUS_COLORS,
          },
        });
      });

      pdf.save(exportFilename({ job: jobNumber, reportSlug: `GA_Dashboard_${dashboard.name || 'export'}`, ext: 'pdf' }));
      setShowPdfModal(false);
      if (failed) {
        alert(`${failed} of ${captures.length} page${captures.length === 1 ? '' : 's'} could not be captured. A placeholder was inserted.`);
      }
    } catch (e) {
      console.error('PDF export failed:', e);
      alert(e.message || 'PDF export failed');
    } finally {
      setExporting(false);
      setPdfProgress(null);
    }
  };

  const toggleMarkerField = (key) => {
    if (!selectedMarker) return;
    const current = markerShowFields(selectedMarker);
    const next = { ...current, [key]: !current[key] };
    updateMarker(selectedMarker.id, { style: { ...selectedMarker.style, showFields: next } });
  };

  const setSelectedBalloonScale = (scale, persist) => {
    if (!selectedMarker) return;
    const nextStyle = { ...selectedMarker.style, balloonScale: clampBalloonScale(scale) };
    setData((prev) => (prev ? {
      ...prev,
      markers: prev.markers.map((m) => (m.id === selectedMarker.id ? { ...m, style: nextStyle } : m)),
    } : prev));
    if (persist) updateMarker(selectedMarker.id, { style: nextStyle });
  };

  const applyBalloonScaleToSlide = async () => {
    if (!selectedMarker) return;
    const scale = markerBalloonScale(selectedMarker, boardBalloonScale);
    const page = selectedMarker.style?.page || activePage || 1;
    const nextById = {};
    markers.forEach((m) => {
      if ((m.style?.page || 1) === page) nextById[m.id] = { ...m.style, balloonScale: scale };
    });
    setData((prev) => (prev ? {
      ...prev,
      markers: prev.markers.map((m) => (nextById[m.id] ? { ...m, style: nextById[m.id] } : m)),
    } : prev));
    try {
      await Promise.all(Object.entries(nextById).map(([id, style]) =>
        authenticatedApiCall(instance, accounts, 'PUT', `/ga-dashboards/markers/${id}`, { style })
      ));
    } catch (e) {
      console.error('Failed to apply balloon size to slide:', e);
    }
  };

  // Presentation mode uses the browser fullscreen API on the component root
  const togglePresentation = () => {
    if (!presentation) {
      // Fullscreen can be denied (no user gesture / permissions) - present inline anyway
      rootRef.current?.requestFullscreen?.()?.catch?.(() => {});
      setPresentation(true);
      setEditMode(false);
    } else {
      if (document.fullscreenElement) document.exitFullscreen?.()?.catch?.(() => {});
      setPresentation(false);
    }
  };
  useEffect(() => {
    const handler = () => { if (!document.fullscreenElement) setPresentation(false); };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const dashMeta = data?.dashboard || dashboard;
  const hasDrawing = !!dashMeta?.drawing_path;
  const pageCount = pageMeta?.pageCount || Number(dashMeta?.settings?.pageCount) || 1;
  const isSequence = pageCount > 1;
  const pageTitles = pageMeta?.pageTitles || dashMeta?.settings?.pageTitles || [];

  const toolBtn = (id, label, title) => (
    <button
      key={id}
      title={title}
      onClick={() => setTool(id)}
      style={{
        padding: '6px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
        border: '1px solid #cbd5e1', background: tool === id ? '#0f766e' : '#fff',
        color: tool === id ? '#fff' : '#334155', fontWeight: 600,
      }}
    >{label}</button>
  );

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', height: presentation ? '100vh' : 'calc(100vh - 130px)', background: '#f8fafc' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        {!presentation && (
          <button onClick={onBack} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#0f766e', fontWeight: 700 }}>← Dashboards</button>
        )}
        <strong style={{ fontSize: 15 }}>{dashboard.name}</strong>
        {isSequence && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
            <span style={{ fontWeight: 700 }}>Page</span>
            <input
              type="number"
              min={1}
              max={pageCount}
              value={activePage}
              onChange={(e) => setActivePage(Math.min(pageCount, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              style={{ width: 56, padding: '4px 6px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
            />
            <span>of {pageCount}</span>
          </label>
        )}
        {onOpenHelp && (
          <button
            type="button"
            onClick={onOpenHelp}
            title="How to use this board"
            style={{
              padding: '5px 12px', borderRadius: 8, border: '1px solid #99f6e4', background: '#f0fdfa',
              color: '#0f766e', fontWeight: 700, cursor: 'pointer', fontSize: 13,
            }}
          >
            ❓ Help
          </button>
        )}

        {/* P6 XER revision (live Current + dated submissions) — same idea as Press Progress */}
        {p6Revisions.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
            <span style={{ fontWeight: 700 }}>Rev</span>
            <select
              value={rev || ''}
              onChange={(e) => {
                setScheduleSource('p6');
                setSnapshotId(null);
                setRev(e.target.value);
              }}
              style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12, maxWidth: 280 }}
              title="P6 XER revision (live Current or dated import)"
            >
              {p6Revisions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label || r.projShortName}{r.taskCount != null ? ` (${r.taskCount} act.)` : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Imported snapshot fallback (SharePoint / manual XER) */}
        {snapshots.length > 0 && (
          <select
            value={scheduleSource === 'snapshot' ? (snapshotId || '') : ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setScheduleSource('p6');
                setSnapshotId(null);
              } else {
                setScheduleSource('snapshot');
                setSnapshotId(v);
              }
            }}
            style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
            title="Imported schedule snapshot (SharePoint / manual upload)"
          >
            <option value="">{p6Revisions.length ? 'Imported snapshot…' : 'Latest imported update'}</option>
            {snapshots.map((s) => (
              <option key={s.id} value={s.id}>
                {fmt(s.data_date) || fmt(s.created_at)} — {s.file_name} ({s.activity_count} act.)
              </option>
            ))}
          </select>
        )}
        {dataDate && (
          <span style={{ fontSize: 12, background: '#fef9c3', border: '1px solid #eab308', borderRadius: 6, padding: '3px 8px', fontWeight: 700, color: '#854d0e' }}>
            {data?.source === 'p6' ? 'P6' : 'Based on'} {dataDate}{data?.snapshot?.project_short_name ? ` · ${data.snapshot.project_short_name}` : ' update'}
          </span>
        )}

        {/* Compare: P6 Rev vs Rev, or imported snapshot */}
        <select
          value={compareRev ? `rev:${compareRev}` : (compareId ? `snap:${compareId}` : '')}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) { setCompareRev(''); setCompareId(''); return; }
            if (v.startsWith('rev:')) { setCompareRev(v.slice(4)); setCompareId(''); }
            else { setCompareId(v.slice(5)); setCompareRev(''); }
          }}
          style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12, maxWidth: 260 }}
          title="Compare against another P6 revision or imported snapshot"
        >
          <option value="">No comparison</option>
          {p6Revisions.filter((r) => String(r.id) !== String(rev || '')).map((r) => (
            <option key={`rev-${r.id}`} value={`rev:${r.id}`}>vs Rev {r.label || r.projShortName}</option>
          ))}
          {snapshots.filter((s) => String(s.id) !== String(snapshotId || '')).map((s) => (
            <option key={`snap-${s.id}`} value={`snap:${s.id}`}>vs snap {fmt(s.data_date) || fmt(s.created_at)}</option>
          ))}
        </select>

        <select
          value={pctMode}
          onChange={(e) => {
            const v = e.target.value;
            setPctMode(v);
            persistDashboardSettings({ pctMode: v });
          }}
          title="How to roll % for a balloon with multiple activities: Act÷Bud hours (weighted), or simple average of each activity’s physical %."
          style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
        >
          <option value="weighted">% weighted (Act÷Bud)</option>
          <option value="average">% average (phys %)</option>
        </select>

        <div style={{ flex: 1 }} />

        {!presentation && (
          <>
            <button
              onClick={() => { setEditMode(!editMode); setTool('select'); setSelectedMarkerId(null); }}
              style={{
                padding: '6px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 700,
                border: '1px solid #cbd5e1', background: editMode ? '#0f766e' : '#fff', color: editMode ? '#fff' : '#334155',
              }}
            >
              {editMode ? '✓ Done editing' : '✏️ Edit'}
            </button>
            <button onClick={exportPng} disabled={exporting} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff' }}>
              {exporting ? 'Exporting…' : '📷 PNG'}
            </button>
            <button
              onClick={() => setShowPdfModal(true)}
              disabled={exporting}
              style={{ padding: '6px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff' }}
              title="Download every slide, or just this page. Choose a plain drawing or pick header stamps."
            >
              {exporting ? 'Exporting…' : '📄 PDF'}
            </button>
          </>
        )}
        <select
          value={renderQualityId}
          onChange={(e) => changeRenderQuality(e.target.value)}
          title={getRenderQuality(renderQualityId).hint}
          style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12, maxWidth: 160 }}
        >
          {RENDER_QUALITY_OPTIONS.map((o) => (
            <option key={o.id} value={o.id} title={o.hint}>{o.label}</option>
          ))}
        </select>
        <select
          value={labelMode}
          onChange={(e) => changeLabelMode(e.target.value)}
          title="Where the date balloons sit. Drag any balloon to move it — position is saved."
          style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
        >
          <option value="margins">🏷 Balloons: outside</option>
          <option value="auto">🏷 Balloons: on drawing</option>
          <option value="off">🏷 Balloons: hidden</option>
        </select>
        {labelMode !== 'off' && (
          <>
            <BalloonSizeSlider
              label="Size"
              title="Default balloon size for this board. Select a pin in Edit to size one balloon, or apply to this slide."
              value={boardBalloonScale}
              onInput={setBoardBalloonScale}
              onCommit={commitBoardBalloonScale}
            />
            <button
              onClick={resetBalloonPositions}
              title="Snap all balloons back to the automatic layout"
              style={{ padding: '6px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff', color: '#475569' }}
            >
              ↺ Reset balloons
            </button>
          </>
        )}
        <button
          onClick={() => setShowTimeline(!showTimeline)}
          style={{ padding: '6px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: showTimeline ? '#0f766e' : '#fff', color: showTimeline ? '#fff' : '#334155' }}
        >
          📊 Timeline
        </button>
        <button onClick={togglePresentation} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: presentation ? '#0f766e' : '#fff', color: presentation ? '#fff' : '#334155' }}>
          {presentation ? 'Exit' : '🖥 Present'}
        </button>
      </div>

      {!presentation && (
        <div style={{ padding: '6px 14px', fontSize: 12, color: '#334155', background: '#f0fdfa', borderBottom: '1px solid #99f6e4', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>
            <strong>How this works:</strong> choose <strong>Rev</strong> (P6 schedule) → balloons show start / finish / Bud·Act·Rem → drag balloons; arrows point to regions.
            {editMode ? ' Use Bind to link a pin/zone to a WBS or activity.' : ' Click Edit to place pins and bind them.'}
          </span>
          {onOpenHelp && (
            <button type="button" onClick={onOpenHelp} style={{ border: 'none', background: 'none', color: '#0f766e', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0, fontSize: 12 }}>
              Open full Help
            </button>
          )}
          {labelMode !== 'off' && !editMode && (
            <span style={{ color: '#64748b' }}>· Drag balloons anytime · Reset balloons only clears the current mode</span>
          )}
        </div>
      )}

      {/* Edit tools row */}
      {editMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0fdfa', borderBottom: '1px solid #99f6e4', flexWrap: 'wrap' }}>
          {toolBtn('select', '↖ Select / move')}
          {toolBtn('pin', '📍 Pin')}
          {toolBtn('rect', '▭ Zone (drag)')}
          {toolBtn('polygon', '⬠ Polygon (click corners · click 1st point to close)')}
          <div style={{ width: 1, height: 22, background: '#99f6e4' }} />
          <button type="button" onClick={() => chooseDrawingFile('append')} title="Keep every current slide and add this file at the end. Pins stay on their slides." style={{ padding: '6px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff' }}>
            + Add slides
          </button>
          <button type="button" onClick={() => chooseDrawingFile('replace')} title="Swap the whole drawing. A shorter file asks before it replaces a multi-slide board." style={{ padding: '6px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff' }}>
            ⬆ Replace drawing
          </button>
          <button onClick={() => setShowSpBrowser(true)} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff' }}>
            ☁ Pick from SharePoint
          </button>
          <input
            ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files?.[0]) uploadDrawing(e.target.files[0]); e.target.value = ''; }}
          />
          <div style={{ width: 1, height: 22, background: '#99f6e4' }} />
          <span style={{ fontSize: 11, color: '#0f766e', fontWeight: 700 }} title="Bud/Act/Rem source. Start/finish always come from the activity.">
            Labor hours:
          </span>
          <select
            value={unitsMode}
            onChange={(e) => {
              const v = e.target.value;
              setUnitsMode(v);
              persistDashboardSettings({ unitsMode: v });
            }}
            title="Bud/Act/Rem from resource assignments by default (LGL schedules are resource-loaded). Start and finish always come from the activity dates. Switch to activity hours only if a job is not resource-loaded."
            style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #99f6e4', fontSize: 12, background: '#fff' }}
          >
            <option value="taskrsrc">From resource assignments (default)</option>
            <option value="task">From activity hours</option>
          </select>
        </div>
      )}

      {/* Selected marker panel */}
      {editMode && selectedMarker && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fffbeb', borderBottom: '1px solid #fde68a', flexWrap: 'wrap' }}>
          <input
            value={selectedMarker.label || ''}
            onChange={(e) => {
              const label = e.target.value;
              setData((prev) => ({ ...prev, markers: prev.markers.map((m) => (m.id === selectedMarker.id ? { ...m, label } : m)) }));
            }}
            onBlur={(e) => updateMarker(selectedMarker.id, { label: e.target.value })}
            placeholder={displayMarkerLabel(selectedMarker, resolvedById[selectedMarker.id])}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, width: 220 }}
          />
          <button onClick={() => setShowPicker(true)} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#fff' }}>
            🔗 {resolvedById[selectedMarker.id]?.matchedCount > 0
              ? `Bound: ${resolvedById[selectedMarker.id].matchedCount} activities`
              : 'Bind to schedule…'}
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#78716c', fontWeight: 700 }} title="Balloon border (also the pin / zone colour when unbound)">
            Border
            <input
              type="color"
              value={selectedMarker.style?.color || '#0f766e'}
              onChange={(e) => updateMarker(selectedMarker.id, { style: { ...selectedMarker.style, color: e.target.value } })}
              style={{ width: 34, height: 30, border: 'none', cursor: 'pointer', padding: 0 }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#78716c', fontWeight: 700 }} title="Balloon fill colour">
            Fill
            <input
              type="color"
              value={selectedMarker.style?.fillColor || '#ffffff'}
              onChange={(e) => updateMarker(selectedMarker.id, { style: { ...selectedMarker.style, fillColor: e.target.value } })}
              style={{ width: 34, height: 30, border: 'none', cursor: 'pointer', padding: 0 }}
            />
          </label>
          <span style={{ fontSize: 12, color: '#78716c', fontWeight: 700 }} title="Per-marker balloon fields for the selected pin/zone">
            Balloon show:
          </span>
          {SHOW_FIELD_KEYS.map(({ key, label }) => {
            const on = markerShowFields(selectedMarker)[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleMarkerField(key)}
                title={`Toggle ${label} on this balloon only`}
                style={{
                  padding: '4px 8px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600,
                  border: '1px solid #cbd5e1', background: on ? '#0f766e' : '#fff', color: on ? '#fff' : '#475569',
                }}
              >
                {label}
              </button>
            );
          })}
          <BalloonSizeSlider
            label="This balloon"
            title="Size this balloon only. Use This slide to copy it to every pin on the current page."
            value={markerBalloonScale(selectedMarker, boardBalloonScale)}
            onInput={(scale) => setSelectedBalloonScale(scale, false)}
            onCommit={(scale) => setSelectedBalloonScale(scale, true)}
          />
          <button
            type="button"
            onClick={applyBalloonScaleToSlide}
            title="Copy this balloon size to every pin on the current page"
            style={{ padding: '4px 8px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#475569' }}
          >
            This slide
          </button>
          <button onClick={() => deleteMarker(selectedMarker.id)} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 13, cursor: 'pointer', border: '1px solid #fca5a5', background: '#fff', color: '#dc2626' }}>
            🗑 Delete
          </button>
          <button onClick={() => setSelectedMarkerId(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b' }}>✕</button>
        </div>
      )}

      {/* Canvas — paint as soon as the list card told us there is a drawing.
          P6 /resolved can populate balloons after first paint. */}
      {error && !hasDrawing ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>{error}</div>
      ) : !hasDrawing && !loading ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: '#64748b' }}>
          <div style={{ fontSize: 40 }}>🗺</div>
          <div>No GA drawing yet — upload one or pick it from SharePoint.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => chooseDrawingFile('replace')} style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', border: 'none', background: '#0f766e', color: '#fff', fontWeight: 700 }}>⬆ Upload drawing</button>
            <button onClick={() => setShowSpBrowser(true)} style={{ padding: '8px 16px', borderRadius: 8, cursor: 'pointer', border: '1px solid #0f766e', background: '#fff', color: '#0f766e', fontWeight: 700 }}>☁ From SharePoint</button>
          </div>
          <input
            ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files?.[0]) uploadDrawing(e.target.files[0]); e.target.value = ''; }}
          />
        </div>
      ) : !hasDrawing ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>Loading…</div>
      ) : (
        isSequence ? (
          <GaSequenceScroller
            ref={sequenceExportRef}
            dashboardId={dashboard.id}
            drawingVersion={dashboard.updated_at}
            pageCount={pageCount}
            pageTitles={pageTitles}
            fetchBlob={fetchBlob}
            markers={markers}
            resolvedById={resolvedById}
            compareById={compareId ? compareById : null}
            viewDate={viewDate}
            editMode={editMode}
            tool={tool}
            selectedMarkerId={selectedMarkerId}
            onSelectMarker={setSelectedMarkerId}
            onCreateGeometry={createMarker}
            onMoveMarker={(id, geometry) => updateMarker(id, { geometry })}
            onMoveLabel={moveLabel}
            labelMode={labelMode}
            renderQualityId={renderQualityId}
            balloonScale={boardBalloonScale}
            activePage={activePage}
            onActivePage={setActivePage}
            exportRef={stageRef}
          />
        ) : (
        <GaDashboardCanvas
          drawingUrl={drawingUrl}
          previewUrl={previewUrl}
          drawingMime={dashMeta.drawing_mime}
          drawingPage={dashMeta.drawing_page}
          markers={markers}
          resolvedById={resolvedById}
          compareById={compareId ? compareById : null}
          viewDate={viewDate}
          editMode={editMode}
          tool={tool}
          selectedMarkerId={selectedMarkerId}
          onSelectMarker={setSelectedMarkerId}
          onCreateGeometry={createMarker}
          onMoveMarker={(id, geometry) => updateMarker(id, { geometry })}
          onMoveLabel={moveLabel}
          labelMode={labelMode}
          renderQualityId={renderQualityId}
          balloonScale={boardBalloonScale}
          exportRef={stageRef}
        />
        )
      )}

      {/* Mini-Gantt timeline strip */}
      {showTimeline && data?.dashboard?.drawing_path && planWindow && (
        <div style={{ maxHeight: 220, overflowY: 'auto', background: '#fff', borderTop: '1px solid #e2e8f0', padding: '8px 14px' }}>
          {markers.map((m) => {
            const r = resolvedById[m.id];
            if (!r || !r.start || !r.finish) return null;
            const total = Math.max(1, new Date(planWindow.max) - new Date(planWindow.min));
            const left = ((new Date(r.start) - new Date(planWindow.min)) / total) * 100;
            const width = Math.max(0.8, ((new Date(r.finish) - new Date(r.start)) / total) * 100);
            const color = STATUS_COLORS[r.status] || '#64748b';
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ width: 220, fontSize: 11, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'right' }}>
                  {displayMarkerLabel(m, r)}
                </span>
                <div style={{ flex: 1, position: 'relative', height: 16, background: '#f1f5f9', borderRadius: 4 }}>
                  <div style={{ position: 'absolute', left: `${left}%`, width: `${width}%`, top: 2, bottom: 2, background: color, borderRadius: 3, opacity: 0.9 }} />
                  {/* progress fill */}
                  {r.pct > 0 && (
                    <div style={{ position: 'absolute', left: `${left}%`, width: `${(width * Math.min(100, r.pct)) / 100}%`, top: 2, bottom: 2, background: color, borderRadius: 3, filter: 'brightness(0.75)' }} />
                  )}
                  {/* data date / scrub line */}
                  {(viewDate || dataDate) && (() => {
                    const lineDate = viewDate || dataDate;
                    const lx = ((new Date(lineDate) - new Date(planWindow.min)) / total) * 100;
                    if (lx < 0 || lx > 100) return null;
                    return <div style={{ position: 'absolute', left: `${lx}%`, top: -2, bottom: -2, width: 2, background: viewDate ? '#0f766e' : '#dc2626' }} />;
                  })()}
                </div>
                <span style={{ width: 150, fontSize: 10.5, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {String(r.start).slice(0, 10)} → {String(r.finish).slice(0, 10)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Time scrubber + legend */}
      {data?.dashboard?.drawing_path && planWindow && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', background: '#fff', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
          <button
            onClick={() => { if (viewDate === null) setViewDate(planWindow.min); setPlaying(!playing); }}
            style={{ width: 34, height: 34, borderRadius: '50%', border: 'none', background: '#0f766e', color: '#fff', fontSize: 14, cursor: 'pointer' }}
          >
            {playing ? '⏸' : '▶'}
          </button>
          <input
            type="range" min={0} max={dayCount} value={dayIndex}
            onChange={(e) => { setPlaying(false); setDayIndex(Number(e.target.value)); }}
            style={{ flex: 1, minWidth: 160 }}
          />
          <span style={{ fontSize: 13, fontWeight: 700, minWidth: 130, color: viewDate ? '#0f766e' : '#64748b' }}>
            {viewDate ? `As of ${viewDate}` : 'Current status'}
          </span>
          {viewDate && (
            <button onClick={() => { setViewDate(null); setPlaying(false); }} style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
              Reset to live
            </button>
          )}
          <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#475569' }}>
            {Object.entries({ not_started: 'Not started', in_progress: 'In progress', complete: 'Complete', late: 'Late' }).map(([k, label]) => (
              <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: STATUS_COLORS[k], display: 'inline-block' }} />
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      {showPicker && selectedMarker && (
        <ActivityPicker
          jobNumber={jobNumber}
          snapshotId={scheduleSource === 'snapshot' ? snapshotId : null}
          rev={scheduleSource === 'p6' ? rev : null}
          initialBinding={selectedMarker.binding}
          onClose={() => setShowPicker(false)}
          onSave={async (binding, suggestedLabel) => {
            setShowPicker(false);
            const patch = { binding };
            if (suggestedLabel && isPlaceholderMarkerLabel(selectedMarker.label)) {
              patch.label = suggestedLabel;
            }
            await updateMarker(selectedMarker.id, patch);
          }}
        />
      )}

      {showSpBrowser && (
        <SharePointBrowser jobNumber={jobNumber} onPick={pickFromSharePoint} onClose={() => setShowSpBrowser(false)} />
      )}

      <GaPdfExportModal
        open={showPdfModal}
        onClose={() => { if (!exporting) setShowPdfModal(false); }}
        onConfirm={exportPdf}
        exporting={exporting}
        progress={pdfProgress}
        pageCount={pageCount}
        activePage={activePage}
        preview={pdfPreview}
      />
    </div>
  );
};

export default GaDashboardView;
